import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useQueryClient } from '@tanstack/react-query';
import { useStudio, useCreateConcept, useUpdateConcept } from '../../../queries';
import { queryKeys } from '../../../queries/keys';
import { StorytellerService } from '../../../services';
import { readSelectedModel } from '../../../store/agentModelStore';
import { useRequireSignIn } from '../../../components/auth/useRequireSignIn';
import type {
  CreateStoryConceptRequest,
  StoryCharacter,
  StoryConcept,
  StoryConceptDetail,
  StudioParameterPalette,
} from '../../../domain';

interface Props {
  open: boolean;
  studioId: string;
  /** Edit mode — the concept being edited (omit for create). */
  initialConcept?: StoryConcept;
  onClose: () => void;
  /** Called with the concept id after a successful save (± outline generation). */
  onSaved: (conceptId: string) => void;
}

interface FormState {
  name: string;
  premise: string;
  theme: string;
  genre: string;
  voice: string;
  pov: string;
  pacing: string;
  targetAudience: string;
  desiredLength: string;
  characters: StoryCharacter[];
  era: string;
  place: string;
  mood: string;
}

const LENGTHS = ['short-story', 'novella', 'novel'];

function initialForm(concept?: StoryConcept): FormState {
  return {
    name: concept?.name ?? '',
    premise: concept?.premise ?? '',
    theme: concept?.theme ?? '',
    genre: concept?.genre ?? '',
    voice: concept?.voice ?? '',
    pov: concept?.pov ?? '',
    pacing: concept?.pacing ?? '',
    targetAudience: concept?.targetAudience ?? '',
    desiredLength: concept?.desiredLength ?? 'novel',
    characters: concept?.characters ?? [],
    era: concept?.setting?.era ?? '',
    place: concept?.setting?.place ?? '',
    mood: concept?.setting?.mood ?? '',
  };
}

const trimToUndefined = (v: string): string | undefined => (v.trim() ? v.trim() : undefined);

function paletteByNeedle(
  palettes: StudioParameterPalette[] | undefined,
  needle: string,
): StudioParameterPalette | undefined {
  return palettes?.find(
    p => p.name.toLowerCase().includes(needle) || p.kind.toLowerCase().includes(needle),
  );
}

/** A select sourced from a matching studio palette, or a free-text field when none exists. */
const PaletteField: React.FC<{
  label: string;
  needle: string;
  palettes: StudioParameterPalette[] | undefined;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, needle, palettes, value, onChange }) => {
  const palette = paletteByNeedle(palettes, needle);
  if (!palette || palette.swatches.length === 0) {
    return (
      <TextField
        label={label}
        value={value}
        onChange={e => onChange(e.target.value)}
        size="small"
        fullWidth
      />
    );
  }
  // Allow the current value even if it isn't one of the swatches (edit of an
  // older concept) by appending it as an extra option.
  const options = palette.swatches.map(s => ({ value: s.value || s.label, label: s.label }));
  const hasCurrent = value === '' || options.some(o => o.value === value);
  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={e => onChange(e.target.value)}
      size="small"
      fullWidth
    >
      <MenuItem value="">
        <em>none</em>
      </MenuItem>
      {!hasCurrent && <MenuItem value={value}>{value}</MenuItem>}
      {options.map(o => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

/**
 * Create / edit a Story Concept (W-F8). On "Save & Generate Outline" it creates
 * (or updates) the concept, then POSTs `/outline` to produce an agent-generated
 * outline, caches the returned detail, and hands the concept id back to the
 * parent (which opens the Outline view).
 */
export const ConceptEditorModal: React.FC<Props> = ({
  open,
  studioId,
  initialConcept,
  onClose,
  onSaved,
}) => {
  const isEdit = !!initialConcept;
  const { data: studio } = useStudio(studioId);
  const palettes = studio?.parameterPalettes;
  const qc = useQueryClient();

  const [form, setForm] = useState<FormState>(() => initialForm(initialConcept));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const createConcept = useCreateConcept();
  const updateConcept = useUpdateConcept(initialConcept?.id ?? '');
  const { requireSignIn } = useRequireSignIn();

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(f => ({ ...f, [key]: value }));

  const buildRequest = (): CreateStoryConceptRequest => ({
    name: form.name.trim(),
    premise: form.premise.trim(),
    theme: trimToUndefined(form.theme),
    genre: trimToUndefined(form.genre),
    voice: trimToUndefined(form.voice),
    pov: trimToUndefined(form.pov),
    pacing: trimToUndefined(form.pacing),
    targetAudience: trimToUndefined(form.targetAudience),
    desiredLength: form.desiredLength,
    characters: form.characters
      .map(c => ({ name: c.name.trim(), role: c.role.trim(), brief: c.brief.trim() }))
      .filter(c => c.name),
    setting: {
      era: trimToUndefined(form.era),
      place: trimToUndefined(form.place),
      mood: trimToUndefined(form.mood),
    },
  });

  const validate = (): boolean => {
    if (!form.name.trim()) {
      setError('A concept name is required.');
      return false;
    }
    if (!form.premise.trim()) {
      setError('A premise is required.');
      return false;
    }
    setError(null);
    return true;
  };

  const persist = async (): Promise<string> => {
    const req = buildRequest();
    if (isEdit && initialConcept) {
      await updateConcept.mutateAsync(req);
      return initialConcept.id;
    }
    const created = await createConcept.mutateAsync(req);
    return created.id;
  };

  const handleSave = async (withOutline: boolean) => {
    if (!validate()) return;
    if (!requireSignIn()) return;
    setBusy(true);
    try {
      const conceptId = await persist();
      if (withOutline) {
        // Honor the AgentBar's model selection for outline gen too — the BE
        // accepts a per-call model override on GenerateOutlineRequest. Falls
        // through to the seeded chapter-structure-agent model when unset.
        const model = readSelectedModel();
        const detail: StoryConceptDetail = await StorytellerService.generateOutline(
          conceptId,
          model ? { model } : {},
        );
        qc.setQueryData(queryKeys.storytellerConcept(conceptId), detail);
      } else {
        qc.invalidateQueries({ queryKey: queryKeys.storytellerConcept(conceptId) });
      }
      qc.invalidateQueries({ queryKey: queryKeys.storytellerConcepts() });
      onSaved(conceptId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the concept.');
    } finally {
      setBusy(false);
    }
  };

  const addCharacter = () =>
    set('characters', [...form.characters, { name: '', role: '', brief: '' }]);
  const updateCharacter = (i: number, patch: Partial<StoryCharacter>) =>
    set(
      'characters',
      form.characters.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    );
  const removeCharacter = (i: number) =>
    set('characters', form.characters.filter((_, idx) => idx !== i));

  const lengthOptions = useMemo(() => {
    const has = LENGTHS.includes(form.desiredLength);
    return has ? LENGTHS : [form.desiredLength, ...LENGTHS];
  }, [form.desiredLength]);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit concept' : 'New story concept'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={e => set('name', e.target.value)}
            size="small"
            fullWidth
            required
          />
          <TextField
            label="Premise"
            value={form.premise}
            onChange={e => set('premise', e.target.value)}
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
            maxRows={5}
          />
          <TextField
            label="Theme (optional)"
            value={form.theme}
            onChange={e => set('theme', e.target.value)}
            size="small"
            fullWidth
          />

          <Stack direction="row" spacing={1}>
            <PaletteField label="Genre" needle="genre" palettes={palettes} value={form.genre} onChange={v => set('genre', v)} />
            <PaletteField label="Voice" needle="voice" palettes={palettes} value={form.voice} onChange={v => set('voice', v)} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <PaletteField label="POV" needle="pov" palettes={palettes} value={form.pov} onChange={v => set('pov', v)} />
            <PaletteField label="Pacing" needle="pacing" palettes={palettes} value={form.pacing} onChange={v => set('pacing', v)} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <PaletteField
              label="Audience"
              needle="audience"
              palettes={palettes}
              value={form.targetAudience}
              onChange={v => set('targetAudience', v)}
            />
            <TextField
              select
              label="Length"
              value={form.desiredLength}
              onChange={e => set('desiredLength', e.target.value)}
              size="small"
              fullWidth
            >
              {lengthOptions.map(l => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">
              Setting
            </Typography>
          </Divider>
          <Stack direction="row" spacing={1}>
            <TextField label="Era" value={form.era} onChange={e => set('era', e.target.value)} size="small" fullWidth />
            <TextField label="Place" value={form.place} onChange={e => set('place', e.target.value)} size="small" fullWidth />
            <TextField label="Mood" value={form.mood} onChange={e => set('mood', e.target.value)} size="small" fullWidth />
          </Stack>

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">
              Characters
            </Typography>
          </Divider>
          {form.characters.map((c, i) => (
            <Stack direction="row" spacing={1} key={i} alignItems="center">
              <TextField label="Name" value={c.name} onChange={e => updateCharacter(i, { name: e.target.value })} size="small" />
              <TextField label="Role" value={c.role} onChange={e => updateCharacter(i, { role: e.target.value })} size="small" />
              <TextField label="Brief" value={c.brief} onChange={e => updateCharacter(i, { brief: e.target.value })} size="small" fullWidth />
              <IconButton aria-label="Remove character" size="small" onClick={() => removeCharacter(i)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Box>
            <Button size="small" startIcon={<AddIcon />} onClick={addCharacter}>
              Add character
            </Button>
          </Box>

          {error && <Alert severity="warning">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => handleSave(false)} disabled={busy}>
          Save
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSave(true)}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          {busy ? 'Working…' : 'Save & Generate Outline'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
